import React, { useCallback } from 'react';
import { Upload } from 'ksnm-common-ui/lib/Upload';
import { UploadModal } from 'ksnm-common-ui/lib/Upload/UploadModal';

import { toBase64 } from '@src/utils/toBase64';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';

type UploadParamsType = {
  uploadType: 'excel' | 'xml';
  organizationName?: string;
  reportPackageCode: string;
  reportVersionCode: string;
};

interface UploadReportInterface {
  toggleUploadModal: () => void;
  uploadParams?: UploadParamsType;
  openUploadModal: boolean;
}

export const UploadReportModal = ({
  toggleUploadModal,
  uploadParams,
  openUploadModal,
}: UploadReportInterface): JSX.Element => {
  const { uploadExcel, uploadXML, fetch } = publicationRegisterStore;

  const accept =
    uploadParams?.uploadType === 'excel'
      ? '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
      : 'application/xml, text/xml';

  const onSaveExcel = useCallback(
    async (files) => {
      const base64 = await toBase64(files[0]);
      const formatBase64 = typeof base64 === 'string' ? base64.split(',')[1] : '';

      const result = await uploadExcel(
        uploadParams?.organizationName ?? '',
        uploadParams?.reportPackageCode ?? '',
        uploadParams?.reportVersionCode ?? '',
        formatBase64,
      );

      if (result) {
        toggleUploadModal();
        fetch();
      }
    },
    [
      uploadParams?.organizationName,
      uploadParams?.reportPackageCode,
      uploadParams?.reportVersionCode,
      fetch,
      toggleUploadModal,
      uploadExcel,
    ],
  );

  const onSaveXML = useCallback(
    async (files) => {
      await uploadXML({
        packageCode: uploadParams?.reportPackageCode ?? '',
        versionCode: uploadParams?.reportVersionCode ?? '',
        file: files[0],
      });

      toggleUploadModal();
    },
    [uploadXML, uploadParams?.reportPackageCode, uploadParams?.reportVersionCode, toggleUploadModal],
  );

  return (
    <UploadModal toggle={toggleUploadModal} visible={openUploadModal}>
      <Upload
        accept={accept}
        onSave={uploadParams?.uploadType === 'excel' ? onSaveExcel : onSaveXML}
        maxSize={uploadParams?.uploadType === 'excel' ? 52428800 : 15728640} // 50МБ и 15МБ
      />
    </UploadModal>
  );
};
